import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
const { func, bool, shape, string } = proptypes;
import {
  TextField,
  DrawerBody,
  DrawerFooter,
  Dropdown,
  MenuItem,
  FormLabel,
  FormController,
  Button
} from '@one-thd/sui-atomic-components';
import { PROJECT_STATUS_ACTIVE, PROJECT_STATUS_ARCHIVED } from '../project-summary-helper';
import { validateProjectName, validateProjectNumber, validateProjectNumberLength } from '../../helpers';

export const FilterDrawerContent = ({
  open,
  onClose,
  onApplyFilters,
  onResetFilters,
  initialFilters = {}
}) => {
  const [projectName, setProjectName] = useState(initialFilters.projectName || '');
  const [projectNumber, setProjectNumber] = useState(initialFilters.projectNumber || '');
  const [projectStatus, setProjectStatus] = useState(initialFilters.projectStatus || '');
  const [errorMessagingProjectName, setErrorMessagingProjectName] = useState('');
  const [errorMessagingProjectNumber, setErrorMessagingProjectNumber] = useState('');
  const [isEnterPress, setIsEnterPress] = useState(false);

  // Reset form when drawer opens with new initial filters
  useEffect(() => {
    if (open) {
      setProjectName(initialFilters.projectName || '');
      setProjectNumber(initialFilters.projectNumber || '');
      setProjectStatus(initialFilters.projectStatus || '');
    }
  }, [open, initialFilters]);

  const handleNameValidation = (value) => {
    if (isEnterPress) return;
    const { isError, errorMessage } = validateProjectName(value);
    if (isError) {
      setErrorMessagingProjectName(errorMessage);
    } else {
      setErrorMessagingProjectName('');
    }
  };

  const handleProjectNumberValidation = (value) => {
    if (isEnterPress) return;
    const { isError, errorMessage } = validateProjectNumber(value);
    if (isError) {
      setErrorMessagingProjectNumber(errorMessage);
    } else {
      setErrorMessagingProjectNumber('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setIsEnterPress(true);
    } else if (!isEnterPress) {
      setIsEnterPress(false);
    }
  };

  const handleOnNameChange = (event) => {
    const { value } = event.target;
    handleNameValidation(value);
    setProjectName(value);
  };

  const handleOnNumberChange = (event) => {
    const { value } = event.target;
    handleProjectNumberValidation(value);
    setProjectNumber(value);
  };

  const handleApplyFilters = async (submitEvent) => {

    submitEvent.preventDefault();

    const filters = {
      projectName: projectName.trim(),
      projectNumber: projectNumber.trim(),
      projectStatus
    };

    // Validate inputs synchronously before applying filters
    const nameValidation = validateProjectName(projectName);
    const numberValidation = validateProjectNumber(projectNumber);
    const numberLengthValidation = validateProjectNumberLength(projectNumber);

    // Check if any validation failed
    if (nameValidation.isError || numberValidation.isError || numberLengthValidation.isError) {
      // Update error states for UI display
      setErrorMessagingProjectName(nameValidation.errorMessage);
      setErrorMessagingProjectNumber(numberValidation.errorMessage || numberLengthValidation.errorMessage);
      return; // Do not apply filters if there are validation errors
    }

    // Clear any existing error messages if validation passed
    setErrorMessagingProjectName('');
    setErrorMessagingProjectNumber('');

    onApplyFilters(filters);
    onClose();
  };

  const handleClearFilters = () => {
    setProjectName('');
    setProjectNumber('');
    setProjectStatus('');
    setErrorMessagingProjectName('');
    setErrorMessagingProjectNumber('');

    onResetFilters({
      projectName: '',
      projectNumber: '',
      projectStatus: ''
    });
  };

  return (
    <>
      <DrawerBody>
        <div className="sui-flex sui-flex-col sui-gap-6 sui-py-4">
          <div>
            <TextField
              id="project-name-filter"
              label="Project Name"
              placeholder="Enter Project Name"
              type="text"
              onBlur={() => { handleNameValidation(projectName); }}
              value={projectName}
              fullWidth
              onChange={(event) => { handleOnNameChange(event); }}
              onKeyDown={(event) => { handleKeyDown(event); }}
              status={
                errorMessagingProjectName ? 'error' : null
              }
              statusMessage={
                errorMessagingProjectName || ''
              }
              InputProps={{
                inputProps: {
                  'data-testid': 'project-name-filter-input',
                }
              }}
            />
          </div>

          <div>
            <TextField
              id="project-number-filter"
              label="Project Number"
              placeholder="Enter Project Number"
              type="text"
              value={projectNumber}
              fullWidth
              onBlur={() => { handleProjectNumberValidation(projectNumber); }}
              onChange={(event) => { handleOnNumberChange(event); }}
              onKeyDown={(event) => { handleKeyDown(event); }}
              status={
                errorMessagingProjectNumber ? 'error' : null
              }
              statusMessage={
                errorMessagingProjectNumber || ''
              }
              helpMessage="Must be a valid 10-digit Project Number"
              InputProps={{
                inputProps: {
                  'data-testid': 'project-number-filter-input',
                  maxLength: 10,
                  pattern: '[a-zA-Z0-9]*'
                }
              }}
            />
          </div>

          <div>
            <FormController fullWidth>
              <FormLabel
                htmlFor="project-status-filter-dropdown"
                id="project-status-filter-dropdown-label"
              >
                Project Status
              </FormLabel>
              <Dropdown
                id="project-status-filter-dropdown"
                value={projectStatus}
                label="Project Status"
                onChange={(event) => setProjectStatus(event.target.value)}
                placeholder="Select status"
                data-testid="project-status-filter-dropdown"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value={PROJECT_STATUS_ACTIVE}>Active</MenuItem>
                <MenuItem value={PROJECT_STATUS_ARCHIVED}>Archived</MenuItem>
              </Dropdown>
            </FormController>
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter position="center">
        <div className="sui-flex sui-flex-col sui-gap-2">
          <Button
            data-testid="filter-drawer-submit-button"
            variant="primary"
            fullWidth
            onClick={handleApplyFilters}
            type="submit"
          > Apply
          </Button>
          <Button
            data-testid="filter-drawer-reset-button"
            variant="secondary"
            fullWidth
            onClick={handleClearFilters}
            type="button"
          > Reset
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

FilterDrawerContent.displayName = 'FilterDrawerContent';

FilterDrawerContent.propTypes = {
  open: bool,
  onClose: func,
  onApplyFilters: func,
  onResetFilters: func,
  initialFilters: shape({
    projectName: string,
    projectNumber: string,
    projectStatus: string
  })
};

FilterDrawerContent.defaultProps = {
  open: false,
  initialFilters: {},
  onClose: () => {},
  onApplyFilters: () => {},
  onResetFilters: () => {}
};
